import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type {
  ProductCollectionMembership,
  ProductCollectionsResponse,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

import { useSetProductCollections } from './use-collection-mutations';
import { productCollectionsQueryOptions } from './use-product-collections';

const NONE: readonly ProductCollectionMembership[] = [];

const sameIds = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every((id) => b.has(id));

const memberIds = (items: readonly ProductCollectionMembership[]) =>
  new Set(items.filter((i) => i.kind === 'manual' && i.member).map((i) => i.id));

/** `body.collectionIds.N` errors, mapped back to the collection id that was sent at N. */
function refusedById(error: unknown, sent: readonly string[]): Map<string, string> {
  const refused = new Map<string, string>();
  if (!isApiError(error)) return refused;
  for (const detail of error.details) {
    const match = /^body\.collectionIds\.(\d+)$/.exec(detail.field);
    const id = match ? sent[Number(match[1])] : undefined;
    if (id) refused.set(id, detail.code);
  }
  return refused;
}

export interface ProductCollectionsDraft {
  storeId: string | undefined;
  query: UseQueryResult<ProductCollectionsResponse>;
  manual: readonly ProductCollectionMembership[];
  automated: readonly ProductCollectionMembership[];
  /** Manual collections the product is in, as edited and not yet saved. */
  selected: ReadonlySet<string>;
  toggle: (id: string) => void;
  dirty: boolean;
  saving: boolean;
  /** The last save's failure, rendered by the card (the mutation is `silentError`). */
  error: unknown;
  /** Collection id to the code the backend refused it with (422 by field index). */
  refused: ReadonlyMap<string, string>;
  canWrite: boolean;
  save: () => Promise<void>;
  discard: () => void;
}

/**
 * Manual collection memberships of one product in one store, as an unsaved draft. Like the
 * gallery draft, it joins the save bar so the page's Save writes it next to the form, and the
 * page owns it, so a form that remounts after its own save keeps the edits.
 */
export function useProductCollectionsDraft(
  storeId: string | undefined,
  productId: string,
): ProductCollectionsDraft {
  const { can } = usePermissions();
  const canWrite = can('collections.write');
  const query = useQuery({
    ...productCollectionsQueryOptions(storeId ?? '', productId),
    enabled: storeId !== undefined,
  });
  const mutation = useSetProductCollections(storeId ?? '', productId);
  const { mutateAsync, reset } = mutation;

  const items = query.data?.items ?? NONE;
  const manual = useMemo(() => items.filter((i) => i.kind === 'manual'), [items]);
  const automated = useMemo(() => items.filter((i) => i.kind === 'automated'), [items]);
  const baseline = useMemo(() => memberIds(items), [items]);

  // The draft belongs to one store; switching stores shows that store's saved memberships.
  const [draft, setDraft] = useState<{ storeId: string; ids: Set<string> } | null>(null);
  const selected = draft && draft.storeId === storeId ? draft.ids : baseline;
  const dirty = !sameIds(selected, baseline);
  const [sent, setSent] = useState<string[]>([]);

  const latest = useRef({ selected, manual, storeId });
  useEffect(() => {
    latest.current = { selected, manual, storeId };
  }, [selected, manual, storeId]);

  const toggle = useCallback((id: string) => {
    const { selected: current, storeId: store } = latest.current;
    if (!store) return;
    const ids = new Set(current);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    latest.current = { ...latest.current, selected: ids };
    setDraft({ storeId: store, ids });
  }, []);

  const discard = useCallback(() => {
    setDraft(null);
    reset();
  }, [reset]);

  const save = useCallback(async () => {
    const { selected: current, manual: rows } = latest.current;
    // Collection order as listed, so field indexes in a 422 map back to collections.
    const ids = rows.filter((i) => current.has(i.id)).map((i) => i.id);
    setSent(ids);
    await mutateAsync(ids);
    setDraft(null);
  }, [mutateAsync]);

  useSaveBarEntry({
    dirty,
    saving: mutation.isPending,
    onSave: save,
    onDiscard: discard,
    disabled: !canWrite || storeId === undefined,
  });

  return {
    storeId,
    query,
    manual,
    automated,
    selected,
    toggle,
    dirty,
    saving: mutation.isPending,
    error: mutation.error,
    refused: refusedById(mutation.error, sent),
    canWrite,
    save,
    discard,
  };
}
